import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Card, 
  Typography, 
  Table, 
  Input, 
  InputNumber, 
  Space, 
  Row, 
  Col, 
  Tag,
  Layout,
  Empty,
  Tabs
} from 'antd';
import { 
  PlusOutlined, 
  MinusOutlined, 
  ArrowLeftOutlined, 
  UpOutlined, 
  DownOutlined 
} from '@ant-design/icons';
import { Role } from '../../../Services/auth.service';
import dayjs from 'dayjs';
import GetUserInfo from "../../authenticatie/GetUserInfo";
import AxiosCaller from '../../../authenticatie/AxiosCaller';
import { openNotificationSuccess, openNotificationError } from '../../../Components/Notification/Notification';
import { frFRIntl } from '@ant-design/pro-components';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Header, Content } = Layout;

function VoorstelScreen() {
    const { post, put } = AxiosCaller();
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = GetUserInfo();

    const [voorstel, setVoorstel] = useState(location.state);
    const [voorstel1, setVoorstel1] = useState(null);
    const [voorstel2, setVoorstel2] = useState(null);
    const [voorstel3, setVoorstel3] = useState(null);
    const [maxHeight, setMaxHeight] = useState(0);

    useEffect(() => {
        const updateHeight = () => {
            setMaxHeight(0.7 * document.documentElement.clientHeight);
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    useEffect(() => {
        if (voorstel) {
            const baseVoorstel = {
                klas: voorstel.klas,
                aantalStudenten: voorstel.aantalStudenten,
                opmerking: voorstel.opmerking,
                voorgerecht: voorstel.voorgerecht,
                hoofdgerecht: voorstel.hoofdgerecht,
                nagerecht: voorstel.nagerecht,
                allergenenVoorgerecht: voorstel.allergenenVoorgerecht,
                allergenenHoofdgerecht: voorstel.allergenenHoofdgerecht,
                allergenenNagerecht: voorstel.allergenenNagerecht
            };

            const createVoorstelData = (datum) => ({
                ...baseVoorstel,
                datum,
                aantalVoorgerecht: voorstel.aantalVoorgerecht,
                aantalHoofdgerecht: voorstel.aantalHoofdgerecht,
                aantalNagerecht: voorstel.aantalNagerecht,
                ingredientenVoorgerecht: voorstel.voorgerecht?.producten?.map(ing => ({ 
                    ...ing, 
                    aantal: ing.aantal * voorstel.aantalVoorgerecht 
                })) || [],
                ingredientenHoofdgerecht: voorstel.hoofdgerecht?.producten?.map(ing => ({ 
                    ...ing, 
                    aantal: ing.aantal * voorstel.aantalHoofdgerecht 
                })) || [],
                ingredientenDessert: voorstel.nagerecht?.producten?.map(ing => ({ 
                    ...ing, 
                    aantal: ing.aantal * voorstel.aantalNagerecht 
                })) || []
            });

            setVoorstel1(createVoorstelData(voorstel.datum1));
            if (voorstel.datum2) setVoorstel2(createVoorstelData(voorstel.datum2));
            if (voorstel.datum3) setVoorstel3(createVoorstelData(voorstel.datum3));
        } else {
            openNotificationError("Er is een fout opgetreden met de communicatie met het vorige scherm")
        }
    }, [voorstel]);

    const bestellen = async () => {
        try {
            const voorstellen = [voorstel1, voorstel2, voorstel3].filter(Boolean);
            
            for (const v of voorstellen) {
                if (
                    (v.voorgerecht && v.aantalVoorgerecht <= 0) ||
                    (v.hoofdgerecht && v.aantalHoofdgerecht <= 0) ||
                    (v.nagerecht && v.aantalNagerecht <= 0)
                ) {
                    openNotificationError("Aantal mag niet 0 zijn als een recept is geselecteerd!")
                    return;
                }
            }

            if(voorstel._id){
                const updatedVoorstel = {
                    leerkracht: voorstel.leerkracht,
                    kookDatum: formatDateToSend(voorstel1.datum),
                    klas: voorstel.klas,
                    aantalStudenten: voorstel.aantalStudenten,
                    opmerking: voorstel.opmerking,
                };
                
                if (voorstel1.voorgerecht) {
                    updatedVoorstel.voorgerecht = {
                        naam: voorstel1.voorgerecht.naam,
                        aantal: voorstel1.aantalVoorgerecht,
                        producten: voorstel1.ingredientenVoorgerecht
                    };
                }
                
                if (voorstel1.hoofdgerecht) {
                    updatedVoorstel.hoofdgerecht = {
                        naam: voorstel1.hoofdgerecht.naam,
                        aantal: voorstel1.aantalHoofdgerecht,
                        producten: voorstel1.ingredientenHoofdgerecht
                    };
                }
                
                if (voorstel1.nagerecht) {
                    updatedVoorstel.nagerecht = {
                        naam: voorstel1.nagerecht.naam,
                        aantal: voorstel1.aantalNagerecht,
                        producten: voorstel1.ingredientenDessert
                    };
                }
                
                await put('/voorstel/'+voorstel._id, updatedVoorstel);
            } else {
                const voorstellenToPost = [voorstel1, voorstel2, voorstel3].filter(Boolean);
                
                for (const v of voorstellenToPost) {
                    const newVoorstel = {
                        leerkracht: currentUser._id,
                        kookDatum: formatDateToSend(v.datum),
                        klas: v.klas,
                        aantalStudenten: v.aantalStudenten,
                        opmerking: v.opmerking,
                    };
                    
                    if (v.voorgerecht) {
                        newVoorstel.voorgerecht = {
                            naam: v.voorgerecht.naam,
                            aantal: v.aantalVoorgerecht,
                            producten: v.ingredientenVoorgerecht
                        };
                    }
                    
                    if (v.hoofdgerecht) {
                        newVoorstel.hoofdgerecht = {
                            naam: v.hoofdgerecht.naam,
                            aantal: v.aantalHoofdgerecht,
                            producten: v.ingredientenHoofdgerecht
                        };
                    }
                    
                    if (v.nagerecht) {
                        newVoorstel.nagerecht = {
                            naam: v.nagerecht.naam,
                            aantal: v.aantalNagerecht,
                            producten: v.ingredientenDessert
                        };
                    }
                    
                    await post('/voorstel', newVoorstel);
                }
            }
            
            if(currentUser.rol === Role.Leerkracht){
                navigate('/LeerKracht/geplandelessen');
            } else if(currentUser.rol === Role.Besteller || currentUser.rol === Role.Gebruikersbeheerder){
                navigate('/Admin/bestelling/geplandelessen');
            }
        } catch (error) {
            console.error('Error plaatsen voorstel:', error);
            openNotificationError("Fout bij plaatsen van voorstel", error)
        }
    };

    const terug = () => {
        voorstel.back = false;
        if(currentUser.rol === Role.Leerkracht){
            navigate('/LeerKracht', { state: voorstel });
        } else if(currentUser.rol === Role.Besteller || currentUser.rol === Role.Gebruikersbeheerder){
            navigate('/admin/bestelling/Leerkracht', { state: voorstel });
        }
    };

    const formatDate = (date) => {
        if (!date) return '';
        
        if (date && date.$isDayjsObject) {
            try {
                return dayjs(date.$d).format('DD/MM/YYYY');
            } catch (error) {
                console.error("Error formatting dayjs date:", error);
                if (date.$d instanceof Date) {
                    const d = date.$d;
                    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                }
            }
        } else if (date instanceof Date) {
            return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
        } else {
            try {
                const dayjsDate = dayjs(date);
                if (dayjsDate.isValid()) return dayjsDate.format('DD/MM/YYYY');
                
                const d = new Date(date);
                if (!isNaN(d.getTime())) {
                    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                }
            } catch (error) {
                console.error("Error parsing date:", error);
            }
            return String(date) || '';
        }
    };

    const formatDateToSend = (date) => {
       
        if (!date) return '';
        
        if (date && date.$isDayjsObject) {
            try {
                return dayjs(date.$d).format('YYYY-MM-DD');
            } catch (error) {
                console.error("Error formatting dayjs date:", error);
                if (date.$d instanceof Date) {
                    const d = date.$d;
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                }
            }
        } else if (date instanceof Date) {
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } else {
            try {
                const dayjsDate = dayjs(date);
                if (dayjsDate.isValid()) return dayjsDate.format('YYYY-MM-DD');
                
                const d = new Date(date);
                if (!isNaN(d.getTime())) {
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                }
            } catch (error) {
                console.error("Error parsing date:", error);
            }
            return String(date) || '';
        }
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Header style={{ background: 'transparent', padding: '12px 24px' }}>
                <Row align="middle">
                    <Button 
                        type="text" 
                        icon={<ArrowLeftOutlined />} 
                        onClick={terug}
                        size="large"
                    />
                    <Title level={2} style={{ margin: '0 16px' }}>Menu overzicht</Title>
                </Row>
            </Header>
           <div style={{ marginTop: '24px' }}>
  <Card className="summary-card">
    <Tabs 
      className="voorstel-tabs"
      defaultActiveKey="1" 
      items={[
        {
          key: '1',
          label: `Datum 1: ${voorstel1 ? formatDate(voorstel1.datum) : 'Laden...'}`,
          children: voorstel1 && (
            <Overzicht 
              voorstel={voorstel1}
              setVoorstel={setVoorstel1}
              bestellen={bestellen}
            />
          )
        },
        ...(voorstel2 ? [{
          key: '2',
          label: `Datum 2: ${voorstel2 ? formatDate(voorstel2.datum) : 'Laden...'}`,
          children: (
            <Overzicht 
              voorstel={voorstel2}
              setVoorstel={setVoorstel2}
              bestellen={bestellen}
            />
          )
        }] : []),
        ...(voorstel3 ? [{
          key: '3',
          label: `Datum 3: ${voorstel3 ? formatDate(voorstel3.datum) : 'Laden...'}`,
          children: (
            <Overzicht 
              voorstel={voorstel3}
              setVoorstel={setVoorstel3}
              bestellen={bestellen}
            />
          )
        }] : [])
      ]}
    />
  </Card>

  <div className="action-buttons">
    <Button 
      type="primary" 
      size="large" 
      className="primary-button"
      onClick={bestellen}
    >
      Alle Bestellingen Plaatsen
    </Button>
  </div>
</div>
        </Layout>
    );
}

const Overzicht = ({ voorstel, setVoorstel, bestellen }) => {
    
    const handleAantalChange = (gerechtType, value) => {
        if (value > 0) {
            setVoorstel((prev) => {
                const updatedVoorstel = {
                    ...prev,
                    [gerechtType]: value,
                };
                
                // Recalculate ingredients when aantal changes
                const multiplier = value;
                
                if (gerechtType === "aantalVoorgerecht" && prev.voorgerecht) {
                    updatedVoorstel.ingredientenVoorgerecht = prev.voorgerecht.producten.map(ing => ({
                        ...ing,
                        aantal: ing.aantal * multiplier
                    }));
                }
                else if (gerechtType === "aantalHoofdgerecht" && prev.hoofdgerecht) {
                    updatedVoorstel.ingredientenHoofdgerecht = prev.hoofdgerecht.producten.map(ing => ({
                        ...ing,
                        aantal: ing.aantal * multiplier
                    }));
                }
                else if (gerechtType === "aantalNagerecht" && prev.nagerecht) {
                    updatedVoorstel.ingredientenDessert = prev.nagerecht.producten.map(ing => ({
                        ...ing,
                        aantal: ing.aantal * multiplier
                    }));
                }
                
                return updatedVoorstel;
            });
        }
    };

    const handleOpmerkingChange = (e) => {
        setVoorstel(prev => ({
            ...prev,
            opmerking: e.target.value
        }));
    };

    return (
        <Content>
            <div style={{ display: 'flex', width: '100%', gap: '16px' }}>
                {voorstel?.voorgerecht && (
                    <Card 
                        style={{ flex: 1 }}
                        title={
                            <Row justify="space-between" align="middle" style={{width: '100%'}}>
                                <Col>{voorstel.voorgerecht.naam}</Col>
                                <Col>
                                    <Space>
                                        <InputNumber
                                            min={1}
                                            value={voorstel.aantalVoorgerecht}
                                            onChange={(value) => handleAantalChange("aantalVoorgerecht", value)}
                                            controls={false}
                                            style={{ width: '50px' }}
                                        />
                                        <Space direction="vertical" size={0}>
                                            <Button
                                                type="text"
                                                icon={<UpOutlined />}
                                                size="small"
                                                onClick={() => handleAantalChange("aantalVoorgerecht", voorstel.aantalVoorgerecht + 1)}
                                            />
                                            <Button
                                                type="text"
                                                icon={<DownOutlined />}
                                                size="small"
                                                onClick={() => handleAantalChange("aantalVoorgerecht", voorstel.aantalVoorgerecht - 1)}
                                                disabled={voorstel.aantalVoorgerecht <= 1}
                                            />
                                        </Space>
                                    </Space>
                                </Col>
                            </Row>
                        }>
                        <IngredientTable
                            ingredienten={voorstel.ingredientenVoorgerecht}
                            setIngredienten={(updater) => {
                                setVoorstel(prev => ({
                                    ...prev, 
                                    ingredientenVoorgerecht: typeof updater === 'function' ? updater(prev.ingredientenVoorgerecht) : updater
                                }));
                            }}
                            allergenen={voorstel.allergenenVoorgerecht}
                        />
                    </Card>
                )}
                
                {voorstel?.hoofdgerecht && (
                    <Card 
                        style={{ flex: 1 }}
                        title={
                            <Row justify="space-between" align="middle" style={{width: '100%'}}>
                                <Col>{voorstel.hoofdgerecht.naam}</Col>
                                <Col>
                                    <Space>
                                        <InputNumber
                                            min={1}
                                            value={voorstel.aantalHoofdgerecht}
                                            onChange={(value) => handleAantalChange("aantalHoofdgerecht", value)}
                                            controls={false}
                                            style={{ width: '50px' }}
                                        />
                                        <Space direction="vertical" size={0}>
                                            <Button
                                                type="text"
                                                icon={<UpOutlined />}
                                                size="small"
                                                onClick={() => handleAantalChange("aantalHoofdgerecht", voorstel.aantalHoofdgerecht + 1)}
                                            />
                                            <Button
                                                type="text"
                                                icon={<DownOutlined />}
                                                size="small"
                                                onClick={() => handleAantalChange("aantalHoofdgerecht", voorstel.aantalHoofdgerecht - 1)}
                                                disabled={voorstel.aantalHoofdgerecht <= 1}
                                            />
                                        </Space>
                                    </Space>
                                </Col>
                            </Row>
                        }>
                        <IngredientTable
                            ingredienten={voorstel.ingredientenHoofdgerecht}
                            setIngredienten={(updater) => {
                                setVoorstel(prev => ({
                                    ...prev, 
                                    ingredientenHoofdgerecht: typeof updater === 'function' ? updater(prev.ingredientenHoofdgerecht) : updater
                                }));
                            }}
                            allergenen={voorstel.allergenenHoofdgerecht}
                        />
                    </Card>
                )}
                
                {voorstel?.nagerecht && (
                    <Card 
                        style={{ flex: 1 }}
                        title={
                            <Row justify="space-between" align="middle" style={{width: '100%'}}>
                                <Col>{voorstel.nagerecht.naam}</Col>
                                <Col>
                                    <Space>
                                        <InputNumber
                                            min={1}
                                            value={voorstel.aantalNagerecht}
                                            onChange={(value) => handleAantalChange("aantalNagerecht", value)}
                                            controls={false}
                                            style={{ width: '50px' }}
                                        />
                                        <Space direction="vertical" size={0}>
                                            <Button
                                                type="text"
                                                icon={<UpOutlined />}
                                                size="small"
                                                onClick={() => handleAantalChange("aantalNagerecht", voorstel.aantalNagerecht + 1)}
                                            />
                                            <Button
                                                type="text"
                                                icon={<DownOutlined />}
                                                size="small"
                                                onClick={() => handleAantalChange("aantalNagerecht", voorstel.aantalNagerecht - 1)}
                                                disabled={voorstel.aantalNagerecht <= 1}
                                            />
                                        </Space>
                                    </Space>
                                </Col>
                            </Row>
                        }>
                        <IngredientTable
                            ingredienten={voorstel.ingredientenDessert}
                            setIngredienten={(updater) => {
                                setVoorstel(prev => ({
                                    ...prev, 
                                    ingredientenDessert: typeof updater === 'function' ? updater(prev.ingredientenDessert) : updater
                                }));
                            }}
                            allergenen={voorstel.allergenenNagerecht}
                        />
                    </Card>
                )}
            </div>
            
            <div style={{ marginTop: '24px' }}>
                <Card title="Opmerkingen" style={{ width: '100%' }}>
                    <TextArea
                        placeholder="Geef hier in wat U extra nodig heeft of andere opmerkingen"
                        value={voorstel?.opmerking}
                        onChange={handleOpmerkingChange}
                        rows={4}
                    />
                </Card>
            </div>
        </Content>
    );
};
const IngredientTable = ({ ingredienten, setIngredienten, allergenen }) => {

    
    const safeIngredienten = Array.isArray(ingredienten) ? ingredienten : [];
    
    const adjustAantal = (index, product, operation) => {

        
        setIngredienten((prevData) => {
  
            const safeData = Array.isArray(prevData) ? prevData : [];
            const newData = safeData.map((item, i) =>
                i === index
                    ? { 
                        ...item, 
                        aantal: Math.max(0, item.aantal + (operation === "plus" ? product.minimumHoeveelheid : -product.minimumHoeveelheid)) 
                      }
                    : item
            );
            
            return newData;
        });
    };
    
    if (!safeIngredienten || safeIngredienten.length === 0) {
        return <Empty description="Geen data beschikbaar" />;
    }
    
    const columns = [
        {
            title: 'Product',
            dataIndex: 'product',
            key: 'product',
            render: (product) => product?.naam
        },
        {
            title: 'Aantal',
            dataIndex: 'aantal',
            key: 'aantal',
            width: 200,
            align: 'right',
            render: (aantal, record, index) => {
                const containsAllergen = record.product?.allergenen?.some(allergen =>
                    allergenen?.includes(allergen.naam)
                );
                return (
                    <Row justify="end" align={'middle'} gutter={8}>
                        <Col>
                            <Text
                                style={{
                                    color: containsAllergen ? '#ff4d4f' : 'inherit',
                                    fontWeight: containsAllergen ? 'bold' : 'normal',
                                }}
                            >
                                {Number(aantal) % 1 === 0 ? aantal : Math.ceil(Number(aantal))} {record.product?.eenheid?.naam}
                            </Text>
                            {containsAllergen && <Tag color="error" style={{ marginLeft: 8 }}>Allergeen</Tag>}
                        </Col>
                        <Col>
                            <Space direction="vertical" size={0}>
                                <Button
                                    icon={<PlusOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        adjustAantal(index, record.product, "plus");
                                    }}
                                    size="small"
                                />
                                <Button
                                    icon={<MinusOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        adjustAantal(index, record.product, "minus");
                                    }}
                                    disabled={record.aantal <= 0}
                                    size="small"
                                />
                            </Space>
                        </Col>
                    </Row>
                );
            }
        }
    ];
    
    const getRowClassName = (record) => {
        const containsAllergen = record.product?.allergenen?.some(allergen =>
            allergenen?.includes(allergen.naam)
        );
        return containsAllergen ? 'allergen-row' : '';
    };
    
    return (
        <Table
            dataSource={safeIngredienten}
            columns={columns}
            pagination={false}
            rowKey={(record, index) => `${record.product?.id || index}`}
            rowClassName={getRowClassName}
        />
    );
};

export default VoorstelScreen;